import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import { Lead } from 'models/Leads';
import {
  PreAuthorization,
  PreAuthorizationStatus,
} from 'models/securityDeposits/PreAuthorizations';
import { PriceDetailsList } from '../../OrderViewPriceDetails.styles';
import OrderViewPriceDetailsListItem from '../../OrderViewPriceDetailsListItem';
import usePreAuthorizationChargeModal from './usePreAuthorizationChargeModal';
import usePreAuthorizationReleaseModal from './usePreAuthorizationReleaseModal';

const SecurityDepositPreAuthorization = ({
  lead,
  preAuthorization,
}: {
  lead: Lead;
  preAuthorization: Pick<
    PreAuthorization,
    'status' | 'startAt' | 'expiresAt' | 'currency' | 'remainingAmount' | 'uid'
  >;
}) => {
  const { t } = useTranslation();
  const { openPreAuthorizationChargeModal } = usePreAuthorizationChargeModal();
  const { openPreAuthorizationReleaseModal } = usePreAuthorizationReleaseModal({
    leadUid: lead.uid,
  });

  if (
    [
      PreAuthorizationStatus.EXPIRED,
      PreAuthorizationStatus.RELEASED,
      PreAuthorizationStatus.FULLY_CHARGED,
    ].includes(preAuthorization.status)
  ) {
    return null;
  }

  const label = t('componentOrder.securityDepositPreAuthorization.label', {
    context: preAuthorization.status,
    startDate: preAuthorization.startAt
      ? parseISO(preAuthorization?.startAt)
      : '-',
    endDate: preAuthorization.expiresAt
      ? parseISO(preAuthorization?.expiresAt)
      : '-',
  });

  const showChargeButton = [
    PreAuthorizationStatus.ACTIVE,
    PreAuthorizationStatus.PARTIALLY_CHARGED,
  ].includes(preAuthorization.status);

  const showReleaseButton = [PreAuthorizationStatus.ACTIVE].includes(
    preAuthorization.status,
  );

  return (
    <PriceDetailsList $hasNoBorder data-testid="price-details-list-deposit">
      <OrderViewPriceDetailsListItem
        title={t('componentOrder.deposit')}
        priceDetailsItems={[
          {
            label: (
              <>
                <span data-testid="price-details-list-item-title-label">
                  {label}
                </span>
                {showChargeButton && (
                  <Button
                    bsStyle="link"
                    data-testid="charge-button"
                    onClick={() =>
                      openPreAuthorizationChargeModal({
                        lead,
                        preAuthorization,
                      })
                    }
                  >
                    {t('componentOrder.securityDepositPreAuthorization.charge')}
                  </Button>
                )}
                {showReleaseButton && (
                  <Button
                    bsStyle="link"
                    data-testid="release-button"
                    onClick={() =>
                      openPreAuthorizationReleaseModal({
                        preAuthorizationUid: preAuthorization.uid,
                      })
                    }
                  >
                    {t(
                      'componentOrder.securityDepositPreAuthorization.release',
                    )}
                  </Button>
                )}
              </>
            ),
            amount: preAuthorization.remainingAmount ?? 0,
            isTotalAmount: false,
            isEditable: false,
            currency: preAuthorization.currency,
          },
        ]}
      />
    </PriceDetailsList>
  );
};

export default SecurityDepositPreAuthorization;
